import { ChangeDetectionStrategy, Component, computed, Input, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { PORTAL_ROLES } from '@guards/portal-role.guard';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { CurrentUserService } from '@services/auth/current-user.service';
import { LanguageDetectionService } from '@services/domain/language-detection.service';
import { PortalSettingsService } from '@services/merit/portal-settings.service';
import { UserService } from '@services/user/user.service';

@Component({
	selector: 'app-lang-switcher',
	imports: [TranslocoModule],
	templateUrl: './lang-switcher.component.html',
	styleUrl: './lang-switcher.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LangSwitcherComponent {
	@Input() isFloating = false; // Floating or inline mode

	private transloco = inject(TranslocoService);
	private router = inject(Router);
	private languageDetectionService = inject(LanguageDetectionService);
	private currentUserService = inject(CurrentUserService);
	private userService = inject(UserService);
	private portalSettings = inject(PortalSettingsService);

	// Signal for active language
	activeLang = signal(this.transloco.getActiveLang());

	// Signal for floating menu state
	isFloatingMenuOpen = signal(false);

	// Computed to check if language is active
	isActive = computed(() => (lang: string) => this.activeLang() === lang);

	constructor() {
		// El observable lo posee el TranslocoService **root**, así que sin cortar
		// la suscripción cada instancia destruida quedaba retenida — y este
		// componente sale en login, topbar, sidebar y los dos portales.
		this.transloco.langChanges$.pipe(takeUntilDestroyed()).subscribe((lang) => {
			this.activeLang.set(lang);
		});
	}

	setLang(lang: string) {
		if (lang === this.transloco.getActiveLang()) return;
		this.transloco.setActiveLang(lang);
		this.activeLang.set(lang);
		this.persistLanguagePreference(lang);
		this.remountCurrentRoute();
	}

	/**
	 * Vuelve a montar la pantalla actual (03/09 · 38). El pipe `transloco` se
	 * actualiza solo, pero lo que una pantalla traduce en código al arrancar
	 * —títulos de página, listas de opciones, acciones del menú— se quedaba en
	 * el idioma anterior hasta recargar. Re-navegar a la misma URL rehace el
	 * componente sin perder la sesión ni recargar la aplicación entera.
	 */
	private remountCurrentRoute(): void {
		const url = this.router.url;
		void this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => this.router.navigateByUrl(url));
	}

	private persistLanguagePreference(lang: string): void {
		// Always update localStorage immediately
		this.currentUserService.updatePreferredLanguage(lang);

		// Signed out (login, company signup): the browser is the only store.
		const userId = this.currentUserService.getUserId();
		if (!userId) return;

		// Two different routes for the same preference. `PUT /users/:id` sits
		// behind StaffOnlyGuard, so a candidate or a company user got a 403 on
		// every switch and their choice never reached the server.
		const request = this.isPortalAccount()
			? this.portalSettings.updateMyLanguage(lang)
			: this.userService.updateUser(String(userId), { preferredLanguage: lang });

		request.subscribe({
			error: (err) => {
				console.error('Error saving language preference:', err);
			},
		});
	}

	/** Candidate and company accounts: everything that is not the backoffice. */
	private isPortalAccount(): boolean {
		const role = (this.currentUserService.getCurrentUser()?.role?.name ?? '').toLowerCase();
		return PORTAL_ROLES.includes(role);
	}

	// Methods for floating mode
	toggleFloatingMenu() {
		this.isFloatingMenuOpen.set(!this.isFloatingMenuOpen());
	}

	selectLanguage(lang: string) {
		this.setLang(lang);
		this.isFloatingMenuOpen.set(false); // Close menu after selecting
	}

	getCurrentFlag(): string {
		const lang = this.activeLang();
		if (lang === 'es') return '🇪🇸';
		return '🇬🇧';
	}

	// Detect system language
	async detectSystemLanguage() {
		await this.languageDetectionService.detectAndSetLanguage();
	}

	// Get system language (for display only)
	async getSystemLanguage(): Promise<string> {
		return await this.languageDetectionService.getCurrentSystemLanguage();
	}
}
